import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { ChartViewKey, subscriptionTags, ViewType } from '../../assets/constants'
import { Security } from '../../proto/generated/security'
import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import websocketActions from '../../actions/websocket.actions'
import { getOverviewWidget } from '../../services/apis/securities'
import { getExchangeAndCodeFromKey, getSecurityAsync, getSecurityKey, isFnOSecurity } from '../../util/security'
import { matchPath, useLocation } from 'react-router-dom'
import ChartWrapper from '../chartV2/chartWrapper'
import OverviewValue from '../screener/OverviewValue'
import { selectOverviewSecurity } from '../../selectors/security'
import { selectChartDateRanges } from '../../selectors/chart'
import { exchangeFromJSON } from '../../proto/generated/common'
import { fetchSecuritySnapshot, fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { selectPrimarySecurity } from '../../selectors/home'
import { selectCommonStore } from '../../selectors/common'
import { WidgetListItems } from '../../util/sidebar'
import ROUTES from '../../assets/routes'
import useSelectView from '../../hooks/useSelectView'
import SecurityOverviewSkeleton from './SecurityOverviewSkeleton'
import SubtleShimmer from '../common/SubtleShimmer'
import { OverviewWidgetResponse_Field, OverviewWidgetResponse_FieldGroup } from '../../proto/generated/overview-widget'
import SecurityOverviewHeader from './SecurityOverviewHeader'
import IndexComponents from './IndexComponents'
import { makeStyles } from 'tss-react/mui'
import ThinArrowUpwardIcon from '../../assets/icons/ThinArrowUpwardIcon'
import ThinArrowDownwardIcon from '../../assets/icons/ThinArrowDownwardIcon'
import SecurityRange from './SecurityRange.tsx'
import SurveillanceIndicatorCard from './SurveillanceIndicatorCard'

const useStyles = makeStyles()((theme) => ({
    stickyHeader: {
        zIndex: 99
    },
    headerLabel: {
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: 500,
        textTransform: 'uppercase',
        background: theme.palette.background.paper
    },
    gridContainer: {
        padding: theme.spacing(2)
    },
    fieldName: {
        color: theme.palette.text.secondary,
        fontSize: '10px',
        textTransform: 'uppercase'
    },
    scrollableDiv: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        borderRadius: '6px',
        overflowY: 'auto'
    },
    mainContainer: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderTopRightRadius: '6px'
    },
    stickyPaper: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        zIndex: 10
    },
    chartContainerWrap: {
        height: '250px',
        width: '100%'
    },
    contentBox: {
        overflowY: 'auto'
    },
    fullWidthContainer: {
        width: '100%'
    }
}))

export const indexSortMenu: any[] = [
    {
        label: 'Symbol',
        value: 'ascending_symbol',
        icon: <ThinArrowUpwardIcon fontSize={'small'} />
    },
    {
        label: 'Change',
        value: 'descending_change',
        icon: <ThinArrowDownwardIcon fontSize={'small'} />
    },
    {
        label: 'Contribution',
        value: 'descending_contribution',
        icon: <ThinArrowDownwardIcon fontSize={'small'} />
    },
    {
        label: 'Weightage',
        value: 'descending_weightage',
        icon: <ThinArrowDownwardIcon fontSize={'small'} />
    }
]

function SecurityOverview() {
    const { expressionMode, primary, underlying, expressionSecurities } = useSelector(selectPrimarySecurity)
    const expressionSecuritiesMemoized = useMemo(() => {
        if (expressionSecurities.length > 0) return expressionSecurities
        return undefined
    }, [expressionSecurities])
    const { rightSideWidget } = useSelector(selectCommonStore)
    const { fieldGroups, indexComponents } = useSelector((state: RootState) => state.overviewStore)
    const screenerSecurity = useSelector(selectOverviewSecurity)
    const dateRanges = useSelector(selectChartDateRanges)
    const overviewDateRange = useMemo(() => dateRanges?.find(r => r.code === 'ONE_DAY'), [dateRanges])
    const dispatch = useDispatch()
    const [loading, setLoading] = useState<boolean>(false)
    const view = useSelectView()
    const location = useLocation()
    const showChart: boolean = useMemo(() => ![ViewType.CHART, ViewType.SYMBOL, ViewType.SPREAD].includes(view), [view, location.pathname])
    const scrollRef = useRef(null)
    const [primarySecurity, setPrimarySecurity] = useState<Security | undefined>(undefined)
    useEffect(() => {
        if (!expressionMode) {
            const result = primary && isFnOSecurity(primary) ? underlying : primary
            setPrimarySecurity(result)
            return
        }
        if (!expressionSecuritiesMemoized?.length) {
            setPrimarySecurity(undefined)
            return
        }
        const sec = expressionSecuritiesMemoized.find((s) => !isFnOSecurity(s))
        if (sec) {
            setPrimarySecurity(sec)
            return
        }
        const underlyingKey = expressionSecuritiesMemoized[0].underlyingSecurityCode
            ? getSecurityKey(expressionSecuritiesMemoized[0].exchange, expressionSecuritiesMemoized[0].underlyingSecurityCode)
            : null
        if (!underlyingKey) {
            setPrimarySecurity(undefined)
            return
        }
        getSecurityAsync(underlyingKey).then((underlyingSec) => {
            if (underlyingSec) {
                setPrimarySecurity(underlyingSec)
            } else {
                fetchSecuritySnapshotsBulk({
                    securities: [{ exchange: expressionSecuritiesMemoized[0].exchange, code: expressionSecuritiesMemoized[0].underlyingSecurityCode ?? '' }],
                    dispatch
                })
            }
        })
    }, [expressionMode, expressionSecuritiesMemoized, primary, underlying])
    const { classes } = useStyles()
    const [currentVisibleOverviewSecurity, setCurrentVisibleOverviewSecurity] = useState<Security | undefined>(primarySecurity)
    const niftyFallbackRun = useRef(false)

    useEffect(() => {
        ;(async () => {
            if (rightSideWidget === WidgetListItems.OVERVIEW && currentVisibleOverviewSecurity) {
                try {
                    await getOverviewWidget({
                        exchange: currentVisibleOverviewSecurity.exchange,
                        code: currentVisibleOverviewSecurity.code,
                        dispatch: dispatch
                    })
                } catch (error) {
                    console.error('Error fetching overview data', error)
                }
            } else {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: [
                        {
                            tag: subscriptionTags.INDEX_COMPONENT,
                            securities: []
                        }
                    ]
                })
            }
        })()
    }, [currentVisibleOverviewSecurity, rightSideWidget])

    // Sync from primarySecurity when it changes - primary takes precedence over fallback (except when on screener)
    useEffect(() => {
        if (matchPath(ROUTES.screener, location.pathname)) return
        if (!primarySecurity) return
        if (primarySecurity.code === currentVisibleOverviewSecurity?.code) return
        setCurrentVisibleOverviewSecurity(primarySecurity)
        niftyFallbackRun.current = false
    }, [primarySecurity, location.pathname, currentVisibleOverviewSecurity?.code])

    // NIFTY_50 fallback: only when truly empty, defer to avoid racing with async primarySecurity
    useEffect(() => {
        if (matchPath(ROUTES.screener, location.pathname)) return
        if (primarySecurity) return
        if (currentVisibleOverviewSecurity) return
        if (niftyFallbackRun.current) return

        niftyFallbackRun.current = true
        const timer = setTimeout(() => {
            getSecurityAsync('NSE:NIFTY_50').then(sec => {
                if (sec) {
                    setCurrentVisibleOverviewSecurity(prev => (prev ? prev : sec))
                }
            })
        }, 50)
        return () => clearTimeout(timer)
    }, [location.pathname, primarySecurity, currentVisibleOverviewSecurity])

    useEffect(() => {
        if (indexComponents?.length > 0 || fieldGroups?.length > 0) {
            setLoading(false)
        }
    }, [indexComponents, fieldGroups])

    // Only show skeleton when we have no security (initial empty state).
    // Avoid setting loading=true on security switch to prevent skeleton↔content flicker.
    useEffect(() => {
        if (!currentVisibleOverviewSecurity) {
            setLoading(true)
        }
    }, [currentVisibleOverviewSecurity])

    useEffect(() => {
        if (!matchPath(ROUTES.screener, location.pathname) || !screenerSecurity || screenerSecurity === 'UNRECOGNIZED:undefined') return
        setLoading(true)
        getSecurityAsync(screenerSecurity).then((security) => {
            if (security) {
                setCurrentVisibleOverviewSecurity(security)
            } else {
                const [exc, code] = getExchangeAndCodeFromKey(screenerSecurity)
                fetchSecuritySnapshot({
                    security: { exchange: exchangeFromJSON(exc), code: code },
                    dispatch
                }).then((response) => {
                    setCurrentVisibleOverviewSecurity(response.security)
                })
            }
        })
    }, [screenerSecurity])

    return loading ? (
        <SubtleShimmer loading>
            <SecurityOverviewSkeleton />
        </SubtleShimmer>
    ) : (
        <Paper ref={scrollRef} id="scrollableDiv" className={classes.scrollableDiv}>
            {currentVisibleOverviewSecurity && !loading ? (
                <Box className={classes.mainContainer}>
                    <Paper className={classes.stickyPaper}>
                        <Grid container>
                            <SecurityOverviewHeader currentVisibleOverviewSecurity={currentVisibleOverviewSecurity} />
                            <Divider className={classes.fullWidthContainer} />
                        </Grid>
                    </Paper>
                </Box>
            ) : null}
            <Box className={classes.contentBox}>
                {showChart && currentVisibleOverviewSecurity && (
                    <Box className={classes.fullWidthContainer}>
                        <Box className={classes.chartContainerWrap}>
                            <ChartWrapper
                                viewKey={ChartViewKey.OVERVIEW}
                                securityList={[getSecurityKey(currentVisibleOverviewSecurity.exchange, currentVisibleOverviewSecurity.code)]}
                                comparableSecurities={[]}
                                mode="SINGLE"
                                hideActions
                                expression=""
                                disableRealtimeUpdates
                                hideChartFooter
                                showMetadata={false}
                                primary={false}
                                primaryScaleId="LEFT"
                                hideLeftScale={true}
                                hideRightScale={true}
                                disableScroll={true}
                                disableVolumeOnLoad={true}
                                selectedDateRange={overviewDateRange ?? undefined}
                            />
                        </Box>
                    </Box>
                )}
                <IndexComponents />
                <RenderFieldGroups fieldGroups={fieldGroups} currentVisibleOverviewSecurity={currentVisibleOverviewSecurity} />
            </Box>
        </Paper>
    )
}

export default memo(SecurityOverview)

const RenderFieldGroups = memo(({ fieldGroups, currentVisibleOverviewSecurity }: { fieldGroups: any; currentVisibleOverviewSecurity: Security | undefined }) => {
    const { classes, cx } = useStyles()

    const scrollToGivenHeader = useCallback((headerName: string) => {
        const stickyContainer = document.getElementById(headerName)
        const parentContainer = document.getElementById('scrollableDiv')
        const headerHeight = stickyContainer?.parentElement?.offsetHeight || 0
        const parentHeight = parentContainer?.scrollHeight || 0
        const maxScrollTop = parentHeight - headerHeight

        if (maxScrollTop > 0) {
            parentContainer?.scrollTo({
                top: maxScrollTop,
                behavior: 'auto'
            })
        }
    }, [])

    return (
        <>
            {fieldGroups.length > 0 &&
                fieldGroups.map((fieldGroup: OverviewWidgetResponse_FieldGroup, index: number) => (
                    <Box key={fieldGroup.name} boxShadow={0}>
                        <Box onClick={() => scrollToGivenHeader(fieldGroup.name)} className={cx(classes.stickyHeader, 'sticky')}>
                            <Divider />
                            <Typography id={fieldGroup.name} className={classes.headerLabel}>
                                {fieldGroup.name}
                            </Typography>
                        </Box>

                        <Grid container rowSpacing={1.75} className={classes.gridContainer}>
                            {fieldGroup.fields.map((field: OverviewWidgetResponse_Field) => (
                                <Grid item xs={6} key={field.name}>
                                    <Stack>
                                        <Typography variant="caption" className={classes.fieldName}>
                                            {field.name}
                                        </Typography>
                                        <OverviewValue overview={true} fieldData={field.screenerField} text={field.value} />
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>

                        {fieldGroup.name.toLowerCase() === 'performance' && currentVisibleOverviewSecurity && (
                            <Box sx={{ px: 2, py: 1 }}>
                                <SurveillanceIndicatorCard security={currentVisibleOverviewSecurity} />
                                <SecurityRange security={currentVisibleOverviewSecurity} show52WkRange={true} />
                            </Box>
                        )}
                    </Box>
                ))}
        </>
    )
})
